import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Music2,
  X,
  Send,
  AlertCircle,
  Search,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { sendCollabRequest } from '../../lib/notificationService';
import {
  formatInstagramUrl,
  formatYoutubeUrl,
  formatSpotifyUrl,
  normalizeArtistData,
} from '../../lib/artistWebService';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import ArtistCard from './ArtistCard';
import '../../styles/Home.css';
import '../../styles/Auth.css';
import '../../styles/ArtistWebPage.css';

export { formatInstagramUrl, formatYoutubeUrl, formatSpotifyUrl, normalizeArtistData };

export default function ArtistWebPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [avatarErrors, setAvatarErrors] = useState({});

  // Followed artists mapping: { [artistUserId]: boolean }
  const [followedArtists, setFollowedArtists] = useState({});
  const [followLoading, setFollowLoading] = useState({});
  // Real follower counts mapping from artist_follows table: { [artistUserId]: number }
  const [followerCounts, setFollowerCounts] = useState({});

  // Collab modal state
  const [collabModalArtist, setCollabModalArtist] = useState(null);
  const [collabMessage, setCollabMessage] = useState('');
  const [collabProjectType, setCollabProjectType] = useState('Feature / Vocal');
  const [collabSubmitting, setCollabSubmitting] = useState(false);
  const [collabSubmitted, setCollabSubmitted] = useState(false);
  const [collabError, setCollabError] = useState('');

  // 1. Fetch live artists from Supabase (profiles + artist_profiles joined)
  const loadArtists = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setArtists([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setLoadError(null);

      // 1. Fetch live artist profiles from Supabase
      let mergedList = [];
      const { data: rawArtists, error: artistError } = await supabase
        .from('artist_profiles')
        .select('*')
        .eq('is_visible', true)
        .order('created_at', { ascending: false });

      if (artistError) {
        throw artistError;
      }

      if (Array.isArray(rawArtists) && rawArtists.length > 0) {
        const userIds = rawArtists.map((a) => a.user_id).filter(Boolean);

        let rawProfiles = [];
        const resProf = await supabase
          .from('profiles')
          .select('id, name, stage_name, genre, secondary_genre, city, avatar_url, spotify_link, apple_music_link, youtube_music_link, instagram, youtube, star_count, artist_web_about, created_at')
          .in('id', userIds);

        if (resProf.error) {
          // Fallback if extended columns not yet migrated
          const resFallback = await supabase
            .from('profiles')
            .select('id, name, stage_name, genre, city, avatar_url, spotify_link, instagram, youtube, created_at')
            .in('id', userIds);
          rawProfiles = resFallback.data || [];
        } else {
          rawProfiles = resProf.data || [];
        }

        const profileMap = (rawProfiles || []).reduce((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {});

        mergedList = rawArtists.map((a) => {
          const prof = profileMap[a.user_id] || {};
          return normalizeArtistData(a, prof);
        });
      }

      setArtists(mergedList);
    } catch (err) {
      console.error('[ArtistWeb] Exception loading artists:', err);
      setArtists([]);
      setLoadError(err?.message || 'Unable to connect to artist directory. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. Fetch authenticated user's followed artists from artist_follows table
  const loadFollows = useCallback(async () => {
    if (!user?.id || !isSupabaseConfigured()) {
      setFollowedArtists({});
      return;
    }

    try {
      const { data, error } = await supabase
        .from('artist_follows')
        .select('artist_user_id')
        .eq('follower_user_id', user.id);

      if (!error && Array.isArray(data)) {
        const followMap = {};
        data.forEach((f) => {
          if (f.artist_user_id) followMap[f.artist_user_id] = true;
        });
        setFollowedArtists(followMap);
      }
    } catch (err) {
      console.warn('[ArtistWeb] Error loading follows:', err);
    }
  }, [user?.id]);

  // 3. Fetch real follower counts for all artists from artist_follows table
  const loadFollowerCounts = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setFollowerCounts({});
      return;
    }

    try {
      const { data, error } = await supabase
        .from('artist_follows')
        .select('artist_user_id');

      if (!error && Array.isArray(data)) {
        const counts = {};
        data.forEach((f) => {
          if (f.artist_user_id) {
            counts[f.artist_user_id] = (counts[f.artist_user_id] || 0) + 1;
          }
        });
        setFollowerCounts(counts);
      }
    } catch (err) {
      console.warn('[ArtistWeb] Error loading follower counts:', err);
    }
  }, []);

  useEffect(() => {
    loadArtists();
    loadFollowerCounts();
  }, [loadArtists, loadFollowerCounts]);

  useEffect(() => {
    loadFollows();
  }, [loadFollows]);

  // 3. Dynamic genre list extracted from database artist profiles (both primary & secondary)
  // Strictly individual valid genres: never display comma-containing strings
  const availableGenres = useMemo(() => {
    const genreSet = new Set();
    artists.forEach((a) => {
      [a.genre, a.secondary_genre].forEach((g) => {
        if (g && typeof g === 'string') {
          // Split on comma to separate multi-genre entries like "HIP HOP, DISS , HARDCORE"
          const tokens = g.split(',');
          tokens.forEach((token) => {
            const clean = token.trim().toUpperCase();
            // Disallow empty strings and any string that still contains a comma
            if (clean && !clean.includes(',')) {
              genreSet.add(clean);
            }
          });
        }
      });
    });
    // Strict safety filter: ensure absolutely no genre option contains a comma
    const cleanList = Array.from(genreSet).filter((g) => g && !g.includes(',')).sort();
    return ['All', ...cleanList];
  }, [artists]);

  // 4. Combined search & filter logic across primary & secondary genres
  const filteredArtists = useMemo(() => {
    return artists.filter((artist) => {
      const stageName = (artist.stage_name || '').toLowerCase();
      const realName = (artist.name || '').toLowerCase();
      const city = (artist.city || '').toLowerCase();
      const genre = (artist.genre || '').toLowerCase();
      const secondaryGenre = (artist.secondary_genre || '').toLowerCase();
      const bio = (artist.artist_web_about || artist.bio || '').toLowerCase();
      const query = searchTerm.toLowerCase().trim();

      const matchesSearch =
        !query ||
        stageName.includes(query) ||
        realName.includes(query) ||
        city.includes(query) ||
        genre.includes(query) ||
        secondaryGenre.includes(query) ||
        bio.includes(query);

      // Extract all individual clean genres for this artist
      const artistIndividualGenres = [];
      [artist.genre, artist.secondary_genre].forEach((g) => {
        if (g && typeof g === 'string') {
          g.split(',').forEach((sub) => {
            const cleanSub = sub.trim().toUpperCase();
            if (cleanSub && !cleanSub.includes(',')) {
              artistIndividualGenres.push(cleanSub);
            }
          });
        }
      });

      const selectedNormalizedGenre = selectedGenre.trim().toUpperCase();
      const matchesGenre =
        selectedGenre === 'All' ||
        artistIndividualGenres.includes(selectedNormalizedGenre);

      return matchesSearch && matchesGenre;
    });
  }, [artists, searchTerm, selectedGenre]);

  // 5. Follow / Unfollow handler with database persistence
  const handleToggleFollow = async (artist) => {
    if (!user?.id) {
      // Redirect logged-out user to login with return destination
      navigate(`/login?redirect=${encodeURIComponent('/artist-web')}`);
      return;
    }

    const artistUserId = artist.user_id || artist.id;
    if (!artistUserId) return;

    // Prevent self-follow
    if (artistUserId === user.id) {
      return;
    }

    const isCurrentlyFollowing = !!followedArtists[artistUserId];
    const newFollowState = !isCurrentlyFollowing;

    // Optimistic UI updates
    setFollowedArtists((prev) => ({
      ...prev,
      [artistUserId]: newFollowState,
    }));
    setFollowerCounts((prev) => {
      const current = prev[artistUserId] || 0;
      const next = newFollowState ? current + 1 : Math.max(0, current - 1);
      return { ...prev, [artistUserId]: next };
    });
    setFollowLoading((prev) => ({ ...prev, [artistUserId]: true }));

    try {
      if (isSupabaseConfigured()) {
        if (newFollowState) {
          // Insert follow record
          const { error: insertErr } = await supabase.from('artist_follows').upsert(
            {
              follower_user_id: user.id,
              artist_user_id: artistUserId,
              created_at: new Date().toISOString(),
            },
            { onConflict: 'follower_user_id,artist_user_id' }
          );
          if (insertErr) throw insertErr;

          // Create notification for the followed artist
          const followerName = profile?.stage_name || profile?.name || user.email?.split('@')[0] || 'A user';
          await supabase.from('notifications').insert({
            user_id: artistUserId,
            title: 'New Follower',
            message: `${followerName} started following you on SenritsuX!`,
            type: 'general',
            data: { follower_id: user.id, follower_name: followerName },
            is_read: false,
            created_at: new Date().toISOString(),
          });
        } else {
          // Delete follow record
          const { error: deleteErr } = await supabase
            .from('artist_follows')
            .delete()
            .eq('follower_user_id', user.id)
            .eq('artist_user_id', artistUserId);
          if (deleteErr) throw deleteErr;
        }
      }
    } catch (err) {
      console.warn('[ArtistWeb] Follow update failed, reverting:', err);
      // Revert optimistic state on error
      setFollowedArtists((prev) => ({
        ...prev,
        [artistUserId]: isCurrentlyFollowing,
      }));
      setFollowerCounts((prev) => {
        const current = prev[artistUserId] || 0;
        const reverted = isCurrentlyFollowing ? current + 1 : Math.max(0, current - 1);
        return { ...prev, [artistUserId]: reverted };
      });
    } finally {
      setFollowLoading((prev) => ({ ...prev, [artistUserId]: false }));
    }
  };

  // 6. Collab modal handlers
  const handleOpenCollab = (artist) => {
    if (!user?.id) {
      navigate(`/login?redirect=${encodeURIComponent('/artist-web')}`);
      return;
    }
    const targetUserId = artist.user_id || artist.id;
    if (user.id === targetUserId) {
      return;
    }
    setCollabModalArtist(artist);
    setCollabMessage('');
    setCollabProjectType('Feature / Vocal');
    setCollabError('');
    setCollabSubmitted(false);
  };

  const handleSendCollab = async (e) => {
    e.preventDefault();
    if (!collabModalArtist) return;

    const recipientUserId = collabModalArtist.user_id || collabModalArtist.id;
    if (user?.id && user.id === recipientUserId) {
      setCollabError('You cannot send a collaboration request to yourself.');
      return;
    }

    setCollabSubmitting(true);
    setCollabError('');

    try {
      await sendCollabRequest({
        recipientUserId,
        recipientStageName: collabModalArtist.stage_name,
        projectType: collabProjectType,
        message: collabMessage,
        senderUser: user,
        senderProfile: profile,
      });

      setCollabSubmitted(true);
      setTimeout(() => {
        setCollabModalArtist(null);
        setCollabSubmitted(false);
      }, 2200);
    } catch (err) {
      console.error('[ArtistWeb] Collab send error:', err);
      const msg = err.message || '';
      if (
        msg.includes('already sent') ||
        msg.includes('duplicate key') ||
        msg.includes('unique')
      ) {
        setCollabError('Collaboration request already sent.');
      } else if (msg.includes('yourself')) {
        setCollabError('You cannot send a collaboration request to yourself.');
      } else {
        setCollabError(msg || 'Could not send collaboration request. Please try again.');
      }
    } finally {
      setCollabSubmitting(false);
    }
  };

  // 7. Image fallback handler
  const handleAvatarError = (artistId) => {
    setAvatarErrors((prev) => ({ ...prev, [artistId]: true }));
  };

  return (
    <div className="sx-artist-web-root">
      <SEO
        title="Artist WEB | SenritsuX - Music Distribution & Artist Services"
        description="Discover verified independent music artists, singers, rappers, and producers on SenritsuX Artist WEB. Connect, collaborate, and explore authentic indie talent."
        canonical="/artists"
        keywords={['SenritsuX artists', 'independent music artists', 'music producers', 'Artist WEB', 'artist discovery']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'SenritsuX Artist WEB',
          description: 'Official Artist WEB of independent musicians, vocalists, and producers on SenritsuX.',
          url: 'https://senritsux.in/artists',
        }}
      />
      <Navbar />

      <main className="sx-artist-web-main">
        <div className="sx-artist-web-layout">
          {/* Left Column: Hero Title, Subtitle, Search, Dynamic Genre Pills */}
          <aside className="sx-artist-web-sidebar">
            <header className="sx-artist-web-header">
              <div className="sx-artist-web-title-wrap">
                <h1 className="sx-artist-web-title-line1">Discover &amp; Collaborate with</h1>
                <div className="sx-artist-web-title-line2">Artist WEB</div>
              </div>
              <p className="sx-artist-web-subtitle">
                Connect directly with singers, rappers, and producers.
              </p>
            </header>

            {/* Search Input */}
            <div className="sx-artist-web-search-wrap">
              <Search size={16} className="sx-artist-web-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="sx-artist-web-search-input"
                placeholder="Search by artist stage name, name, city, genre, or bio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search artists"
              />
            </div>

            {/* Dynamic Genre Filter Pills */}
            <div className="sx-artist-web-genres" role="group" aria-label="Genre filters">
              {availableGenres.map((g) => {
                const isActive = selectedGenre === g;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setSelectedGenre(g)}
                    className={`sx-genre-pill ${isActive ? 'active' : ''}`}
                    aria-pressed={isActive}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Right Column: Artist Directory Content */}
          <section className="sx-artist-web-content" aria-label="Artists directory">
            {loadError ? (
              <div className="sx-artist-feedback sx-error-card">
                <AlertCircle size={44} color="#ef4444" className="sx-feedback-icon" />
                <h2 className="sx-feedback-title">Unable to Load Artists</h2>
                <p className="sx-feedback-desc">{loadError}</p>
                <button
                  type="button"
                  onClick={() => {
                    loadArtists();
                    loadFollowerCounts();
                  }}
                  className="sx-retry-btn"
                >
                  <RefreshCw size={15} /> Retry
                </button>
              </div>
            ) : loading ? (
              <div className="sx-artist-grid">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <div key={n} className="sx-artist-card-skeleton" aria-hidden="true">
                    <div className="sx-skeleton-avatar" />
                    <div className="sx-skeleton-text sx-skeleton-title" />
                    <div className="sx-skeleton-text sx-skeleton-subtitle" />
                    <div className="sx-skeleton-footer" />
                  </div>
                ))}
              </div>
            ) : filteredArtists.length === 0 ? (
              <div className="sx-artist-feedback sx-empty-card">
                <Music2 size={44} color="#d9fb06" className="sx-feedback-icon" />
                <h2 className="sx-feedback-title">No artists found</h2>
                <p className="sx-feedback-desc">
                  {searchTerm || selectedGenre !== 'All'
                    ? 'Try adjusting your search query or selecting a different genre.'
                    : 'Registered artists will appear automatically here as soon as they complete signup.'}
                </p>
              </div>
            ) : (
              <div className="sx-artist-grid">
                {filteredArtists.map((artist) => {
                  const artistUserId = artist.user_id || artist.id;
                  const isFollowing = Boolean(followedArtists[artistUserId]);
                  const isFollowPending = Boolean(followLoading[artistUserId]);
                  const artistFollowers = followerCounts[artistUserId] || 0;
                  const isSelf = user?.id === artistUserId;

                  return (
                    <ArtistCard
                      key={artist.id}
                      artist={artist}
                      isFollowing={isFollowing}
                      isFollowPending={isFollowPending}
                      followerCount={artistFollowers}
                      isSelf={isSelf}
                      onToggleFollow={handleToggleFollow}
                      onOpenCollab={handleOpenCollab}
                      onAvatarError={handleAvatarError}
                      hasAvatarError={Boolean(avatarErrors[artist.id])}
                    />
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />

      {/* Collab Modal */}
      {collabModalArtist && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => !collabSubmitting && setCollabModalArtist(null)}
        >
          <div
            className="auth-card"
            style={{ maxWidth: '480px', width: '100%', padding: '2rem', position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setCollabModalArtist(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
              }}
            >
              <X size={20} />
            </button>

            <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.4rem' }}>
              Request Collab with <span style={{ color: '#d9fb06' }}>{collabModalArtist.stage_name}</span>
            </h2>
            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem' }}>
              Submit your collaboration proposal. The artist will receive your request directly on SenritsuX.
            </p>

            {collabError && (
              <div className="auth-global-error" style={{ marginBottom: '1rem' }}>
                <AlertCircle size={16} />
                <span>{collabError}</span>
              </div>
            )}

            {collabSubmitted ? (
              <div
                className="auth-global-success"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}
              >
                <CheckCircle2 size={20} color="#d9fb06" />
                <div>
                  <div style={{ fontWeight: 700, color: '#d9fb06' }}>Collaboration Request Sent!</div>
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' }}>
                    {collabModalArtist.stage_name} has received your notification.
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSendCollab}>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="auth-label" style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
                    Project Type
                  </label>
                  <select
                    className="auth-input"
                    value={collabProjectType}
                    onChange={(e) => setCollabProjectType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                    }}
                  >
                    <option value="Feature / Vocal" style={{ background: '#1a1a1a', color: '#fff' }}>
                      Feature / Vocal
                    </option>
                    <option value="Beat Production" style={{ background: '#1a1a1a', color: '#fff' }}>
                      Beat Production
                    </option>
                    <option value="Songwriting / Topline" style={{ background: '#1a1a1a', color: '#fff' }}>
                      Songwriting / Topline
                    </option>
                    <option value="Mixing &amp; Mastering" style={{ background: '#1a1a1a', color: '#fff' }}>
                      Mixing &amp; Mastering
                    </option>
                    <option value="Remix / Co-Production" style={{ background: '#1a1a1a', color: '#fff' }}>
                      Remix / Co-Production
                    </option>
                    <option value="Other Project" style={{ background: '#1a1a1a', color: '#fff' }}>
                      Other Project
                    </option>
                  </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="auth-label" style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
                    Proposal Message / Track Link
                  </label>
                  <textarea
                    className="auth-input"
                    rows={4}
                    placeholder="Describe your vision, deadline, track link, or sound ideas..."
                    value={collabMessage}
                    onChange={(e) => setCollabMessage(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setCollabModalArtist(null)}
                    disabled={collabSubmitting}
                    style={{
                      padding: '0.65rem 1.2rem',
                      borderRadius: '8px',
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={collabSubmitting}
                    style={{
                      padding: '0.65rem 1.4rem',
                      borderRadius: '8px',
                      background: '#d9fb06',
                      border: 'none',
                      color: '#111',
                      cursor: collabSubmitting ? 'wait' : 'pointer',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    {collabSubmitting ? (
                      <>
                        <span className="auth-spinner" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Send Proposal
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
